import { Component, ChangeDetectorRef, OnInit, ViewChild, HostListener, ElementRef, signal } from '@angular/core';
import { LoadingComponent } from '@/components/loading/loading.component';
import { Router } from '@angular/router';
import { FormControl, FormGroup, ReactiveFormsModule, Validators, FormArray } from '@angular/forms';
import { ApiService } from '../../../apiConnection/ApiService';
import { Urls } from '../../../apiConnection/urls';
import { CommonModule } from '@angular/common';
import { CustomInputComponent } from '@/components/custom-input/custom-input.component';
import { CentToEurDisplayDirective } from '@/directives/cent-to-eur-display.directive';
import { AuthenticationService } from '@/apiConnection/authentication.service';
import { CustomSelectComponent } from '@/components/custom-select/custom-select.component';
import { Utils } from '@/utils/utils';
import { DecimalInputDirective } from '@/directives/decimal-input.directive';

@Component({
  selector: 'app-document-form',
  imports: [
    LoadingComponent,
    DecimalInputDirective,
    ReactiveFormsModule,
    CommonModule,
    CustomInputComponent,
    CentToEurDisplayDirective,
    CustomSelectComponent,
  ],
  templateUrl: './document-albaran-form.page.html'
})
export class DocumentAlbaranFormPage implements OnInit {
  get invoiceSplitArray(): FormArray {
    return this.saveForm.get('invoice_split') as FormArray;
  }
  public loading = signal(false);
  public saving = signal(false);
  public saveForm!: FormGroup;
  public dateStart: string | null = null;
  public dateEnd: string | null = null;
  public clientDocumentsList: any[] = [];
  public showResults = false;
  public deliveryNotes: any[] = [];
  public filteredDeliveryNotes: any[] = [];
  public dropdowns: any = {
    clients: [],
    users: [],
    payMethods: [],
    offices: [],
  };

  private codeRetryCount = 0;

  // hidden items dropdown when click outside
  @ViewChild('mainSearch') searchContainer!: ElementRef;
  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (this.searchContainer && !this.searchContainer.nativeElement.contains(event.target)) {
      this.showResults = false;
    }
  }

  constructor(
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private authenticationService: AuthenticationService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.createForm();
    // El número depende de la oficina: se pide cuando getDropdowns() fija la oficina por defecto
    this.getDropdowns(() => this.getIdByCompany());
  }

  get isClientSelected() {
    return this.saveForm.get('client_id')?.value > 0
  }

  createForm() {
    const currentUserId = this.authenticationService.currentUserValue.id

    this.saveForm = new FormGroup({
      id_by_company: new FormControl(null),
      year: new FormControl(new Date().getFullYear(), [Validators.required]),
      date: new FormControl(new Date().toISOString().split('T')[0], [Validators.required]),
      phone: new FormControl(''),
      pay_id: new FormControl(null, [Validators.required]),
      tax_included: new FormControl(1),
      user_id: new FormControl(currentUserId, [Validators.required]),
      discount_absolute_bool: new FormControl(0), // 0 -> %, 1 -> €
      discount: new FormControl(0),
      notes: new FormControl(null),
      client_id: new FormControl(null, [Validators.required]),
      type: new FormControl('factura', [Validators.required]),
      irpf_bool: new FormControl(0),
      subtotal: new FormControl('0.00'),
      total: new FormControl('0.00'),
      items: new FormArray([]),
      invoice_split: new FormArray([]),
      invoice_credit_bool: new FormControl(1), // allways true
      office_id: new FormControl(null),
    });

    this.saveForm.valueChanges.subscribe(() => this.calculateInvoice());

    // El número de la factura depende de la oficina y el año elegidos
    this.saveForm.get('office_id')?.valueChanges.subscribe(() => {
      this.getIdByCompany();
      // Al cambiar de oficina, la lista (y la selección) pasa a los albaranes de la nueva
      if (this.isClientSelected) this.getDeliveryNotes(true);
    });
    this.saveForm.get('year')?.valueChanges.subscribe(() => this.getIdByCompany());

    this.saveForm.get('tariff')?.valueChanges.subscribe(() => {
      this.updateAllItemsPrices();
    });
  }

  get items() { return this.saveForm.get('items') as FormArray; }
  get itemControls() { return this.items.controls; }
  get invoiceSplitConfiguration() {
    if (this.saveForm.get('pay_id')?.value) {
      const payMethod = this.dropdowns.payMethods.filter((pay_method: { id: number }) => pay_method.id == this.saveForm.get('pay_id')?.value)
      if (payMethod.length) {
        return JSON.parse(payMethod[0].configurations);
      }
    }
    return []
  }

  changePayMethod() {
    const config = this.invoiceSplitConfiguration;
    const splitArray = this.saveForm.get('invoice_split') as FormArray;
    splitArray.clear();

    if (config && config.length) {
      config.forEach((c: any) => {
        const baseDateStr = this.saveForm.get('date')?.value;
        const baseDate = baseDateStr ? new Date(baseDateStr + 'T00:00:00') : new Date();
        baseDate.setDate(baseDate.getDate() + Number(c.days));
        const yyyy = baseDate.getFullYear();
        const mm = String(baseDate.getMonth() + 1).padStart(2, '0');
        const dd = String(baseDate.getDate()).padStart(2, '0');
        const dateStr = `${yyyy}-${mm}-${dd}`;
        splitArray.push(new FormGroup({
          date: new FormControl(dateStr),
          percent: new FormControl(c.percent),
          subtotal: new FormControl('0.00'),
          total: new FormControl('0.00'),
        }));
      });

      this.updateInvoiceSplitTotals();

      splitArray.controls.forEach((group: any) => {
        group.get('percent').valueChanges.subscribe(() => {
          this.updateInvoiceSplitTotals();
        });
      });
    }
  }
  updateInvoiceSplitTotals() {
    const subtotal = parseFloat(this.saveForm.get('subtotal')?.value || '0.00');
    const total = parseFloat(this.saveForm.get('total')?.value || '0.00');
    const splitArray = this.saveForm.get('invoice_split') as FormArray;
    splitArray.controls.forEach((group: any) => {
      const percent = parseFloat(group.get('percent').value) / 100;
      const splitSubtotal = (subtotal * percent).toFixed(2);
      const splitTotal = (total * percent).toFixed(2);
      group.get('subtotal').setValue(splitSubtotal, { emitEvent: false });
      group.get('total').setValue(splitTotal, { emitEvent: false });
    });
  }

  getPrice(product: any): number {
    const choosedTariffId = this.saveForm.get('tariff')?.value;
    const tariff = this.dropdowns.tariffs?.find((t: any) => t.id == choosedTariffId);

    if (!tariff || tariff.tax) {
      return product.price || 0;
    }

    try {
      const more_prices = typeof product.more_prices === 'string'
        ? JSON.parse(product.more_prices)
        : product.more_prices;

      const specificPrice = more_prices?.find((p: any) => p.quotient == choosedTariffId);
      return specificPrice ? specificPrice.price / 100 : (product.price || 0);
    } catch (e) {
      return product.price || 0;
    }
  }

  updateAllItemsPrices() {
    this.items.controls.forEach(control => {
      const productId = control.get('product_id')?.value;
      const product = this.dropdowns.products.find((p: any) => p.id == productId);

      if (product) {
        const newPrice = this.getPrice(product);
        control.get('price')?.setValue(newPrice, { emitEvent: false });
      }
    });
    this.calculateInvoice();
  }

  getDropdowns(onComplete?: () => void) {
    const config = { users: {}, pay_methods: {}, clients: {}, companies: {}, offices: {} };
    this.apiService.getSelectors(Urls.urls.selectors, config).subscribe({
      next: (data: any) => {
        this.dropdowns = {
          users: data.users || [],
          payMethods: data.pay_methods || [],
          clients: data.clients || [],
          offices: data.offices || [],
        };

        // Con oficinas, la oficina es obligatoria (el backend numera por oficina y lo valida)
        const officeControl = this.saveForm.get('office_id');
        if (this.dropdowns.offices.length) {
          officeControl?.addValidators(Validators.required);
        }
        officeControl?.setValue(this.dropdowns.offices[0]?.id ?? null, { emitEvent: false });

        this.cdr.detectChanges();
        onComplete?.();
      }
    });
  }

  // Solo vale la última respuesta si cambian oficina/año con una petición en curso
  private idByCompanyRequest = 0;

  getIdByCompany() {
    // Mismo slug que el resto de pantallas; numeración por oficina + año (sin oficinas, por empresa + año)
    const params = new URLSearchParams();
    const officeId = this.saveForm.get('office_id')?.value;
    const year = this.saveForm.get('year')?.value;
    if (officeId) params.set('office_id', officeId);
    if (year) params.set('year', year);

    const request = ++this.idByCompanyRequest;

    this.apiService.getPetition(Urls.urls.documents + '/last-id-by-company/invoice', '?' + params.toString()).subscribe((data: any) => {
      if (request !== this.idByCompanyRequest) return;
      this.saveForm.patchValue({ id_by_company: data.last_id_by_company + 1 }, { emitEvent: false });
      this.cdr.detectChanges();
    });
  }

  getDeliveryNotes(refreshItems: boolean = false) {
    // Solo albaranes de la oficina de la factura (o antiguos sin oficina): el backend
    // rechaza facturar albaranes de otra oficina
    const officeId = this.saveForm.get('office_id')?.value;
    const config = {
      documents: {
        client_id: this.saveForm.get('client_id')?.value,
        type: 'albaran',
        ...(officeId ? { office_or_unassigned: officeId } : {})
      }
    };
    this.apiService.getSelectors(Urls.urls.selectors, config).subscribe({
      next: (data: any) => {
        this.clientDocumentsList = data.documents.map((document: { id: Number, id_by_company: Number, date: Date, total: Number, year: Number }) => {
          const d = new Date(document.date);
          const date_string = d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
          return {
            id: document.id,
            id_by_company: document.id_by_company,
            date_string,
            price: document.total,
            code: document.id_by_company+'/'+document.year,
            date: document.date
          }
        });
        if(refreshItems){
        this.dateFilter();
        this.cdr.detectChanges();
        }
      }
    });
  }

  get show_list() {
    return this.clientDocumentsList.filter((product: any) => {
    const items_list = this.saveForm.get('items')?.value;
    if(items_list.find((item: { product_id: any; }) => item.product_id === product.id)) {
      return false;
    }
      return true;

    });
  };

  filterDeliveryNotes(query: string) {
    if (!query) {
      this.filteredDeliveryNotes = this.deliveryNotes || [];
      return;
    }
    const q = query.toLowerCase();
    this.filteredDeliveryNotes = (this.deliveryNotes || []).filter((p: any) =>
      p.name.toLowerCase().includes(q) ||
      p.id.toString().includes(q) ||
      (p.code && p.code.toLowerCase().includes(q))
    );
  }

  selectDeliveryNoteFromSearch(note: any, inputElement: HTMLInputElement) {
    const existingIndex = this.items.controls.findIndex(
      control => control.get('product_id')?.value == note.id
    );
    if (existingIndex !== -1) {
      const control = this.items.at(existingIndex);
    } else {
      this.addItem({
        product_id: note.id,
        price: note.price,
        discount: 0,
        code: note.code,
        date_string: note.date_string,
      });
    }
    inputElement.value = '';
    this.showResults = false;
    this.filteredDeliveryNotes = this.deliveryNotes ? [...this.deliveryNotes] : [];
    setTimeout(() => this.cdr.detectChanges());
  }

  addItem(itemData: any = null) {
    const itemGroup = new FormGroup({
      product_id: new FormControl(itemData?.product_id || null, [Validators.required]),
      discount: new FormControl(itemData?.discount || 0),
      price: new FormControl(itemData?.price || 0),
      date_string: new FormControl(itemData?.date_string || ''),
      code: new FormControl(itemData?.code || ''),
    });
    this.items.push(itemGroup);
  }

  removeItem(index: number) {
    this.items.removeAt(index);
    this.calculateInvoice();
  }

  calculateInvoice() {
    let sub = 0;
    this.items.controls.forEach(c => {
      const p = c.get('price')?.value || 0;
      sub += p;
    });

    const disc = this.saveForm.get('discount')?.value || 0;
    const isAbsolute = Number(this.saveForm.get('discount_absolute_bool')?.value) === 1;

    let tot = sub;
    if (isAbsolute) {
      tot = sub - (disc * 100);
    } else {
      tot = sub * (1 - (disc / 100));
    }

    if (this.saveForm.get('irpf_bool')?.value) {
      tot += (sub * 0.052);
    }

    this.saveForm.get('subtotal')?.setValue(sub.toFixed(2), { emitEvent: false });
    this.saveForm.get('total')?.setValue(tot.toFixed(2), { emitEvent: false });

    if ((this.saveForm.get('invoice_split') as FormArray).length > 0) {
      this.updateInvoiceSplitTotals();
    }
  }

  saveEntity() {
    if (this.saveForm.valid) {
      this.saving.set(true);
      const payload = {
        ...this.saveForm.value,
        items: this.saveForm.value.items,
        invoice_split: Array.isArray(this.saveForm.value.invoice_split) ? this.saveForm.value.invoice_split : []
      };
      this.apiService.save(Urls.urls.documents, payload, 0).subscribe({
        next: () => {
          this.codeRetryCount = 0;
          this.saving.set(false);
          this.router.navigate(['/sales']);
        },
        error: (error: any) => {
          if (Utils.isCodeAlreadyTakenError(error, 'id_by_company') && this.codeRetryCount < Utils.MAX_CODE_ALREADY_TAKEN_RETRIES) {
            this.codeRetryCount++;
            this.saveForm.get('id_by_company')?.setValue(Utils.incrementCode(this.saveForm.get('id_by_company')?.value));
            this.saveEntity();
            return;
          }

          this.codeRetryCount = 0;
          this.saving.set(false);
          Utils.notification.alert(Utils.extractErrorMessage(error) || 'Error al guardar la factura de crédito');
        }
      });
    }
  }

  changeClient() {
    const id = this.saveForm.get('client_id')?.value;
    const client = this.dropdowns.clients.find((c: any) => c.id == id);
    if (client) {
      this.saveForm.get('phone')?.setValue(client.phone || '');
      this.saveForm.get('pay_id')?.setValue(client.payment_method_id);
      this.changePayMethod();
    }
    this.getDeliveryNotes(true);

  }

  // getDeliveryNoteInfo(noteId: any) {
  //   return this.deliveryNotes.find((n: any) => n.id == noteId);
  // }

  saveAndPrint() {
    if (this.saveForm.valid) {
      this.saving.set(true);
      const payload = {
        ...this.saveForm.value,
        items: this.saveForm.value.items,
        invoice_split: Array.isArray(this.saveForm.value.invoice_split) ? this.saveForm.value.invoice_split : []
      };
      this.apiService.save(Urls.urls.documents, payload, 0).subscribe({
        next: (result: any) => {
          this.codeRetryCount = 0;
          this.saving.set(false);
          const id = result.document.id;
          window.open(`/invoice-credit/print/${id}`, '_blank');
        },
        error: (error: any) => {
          if (Utils.isCodeAlreadyTakenError(error, 'id_by_company') && this.codeRetryCount < Utils.MAX_CODE_ALREADY_TAKEN_RETRIES) {
            this.codeRetryCount++;
            this.saveForm.get('id_by_company')?.setValue(Utils.incrementCode(this.saveForm.get('id_by_company')?.value));
            this.saveAndPrint();
            return;
          }

          this.codeRetryCount = 0;
          this.saving.set(false);
          Utils.notification.alert(Utils.extractErrorMessage(error) || 'Error al guardar la factura de crédito');
        }
      });
    }
  }
  onDateStartChange(event : any) {
    this.dateStart = event;
  }

  onDateEndChange(event : any) {
    this.dateEnd = event;
  }

  dateFilter(){
    this.items.clear();
      this.clientDocumentsList.map((product: any) => {
        const docDate = new Date(product.date);
        const startDate = this.dateStart ? new Date(this.dateStart) : null;
        const endDate = this.dateEnd ? new Date(this.dateEnd) : null;
        if (startDate && docDate < startDate) {
          return ;
        }
        if (endDate && docDate > endDate) {
          return ;
        }
        this.addItem({
          product_id: product.id,
          price: product.price,
          discount: 0,
          code: product.code,
          date_string: product.date_string || '',
        });
    });

  }
}